import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useToast from "~/components/ui/toast";
import { writeNextTurnAgent } from "~/pages/manifold-nextgen/components/conversations/nextTurnAgent";
import { stashPendingComposerDraft } from "~/pages/manifold-nextgen/components/conversations/pendingComposerDraft";
import { nextgenConversationPath, nextgenProjectPath } from "~/pages/manifold-nextgen/paths";
import { invokeQuickAction, mutateQuickActionsAvailable } from "~/services/quickActions";
import { getApiErrorMessage, isApiError } from "~/utils/apiClient";

/**
 * Invokes a quick action and drops the user into the session it opens: the seed
 * prompt (if any) is stashed as a one-shot composer pre-fill and we navigate to
 * the new conversation. If the backend opened no session, fall back to the
 * materialized project page.
 */
export const useInvokeQuickAction = () => {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [invokingId, setInvokingId] = useState<string | null>(null);

  const invoke = async (quickActionId: string) => {
    setInvokingId(quickActionId);
    try {
      const response = await invokeQuickAction(quickActionId);
      if (response.session) {
        // The session has no turns yet, so it has no derivable current agent —
        // seed the next-turn storage with the agent it was opened with, the
        // same path an explicit switch writes to, so the first run still
        // resolves one.
        if (response.agent_id) {
          writeNextTurnAgent(response.session.id, response.agent_id);
        }
        if (response.seed_prompt) {
          stashPendingComposerDraft(response.session.id, response.seed_prompt);
        }
        navigate(nextgenConversationPath(response.project.id, response.session.id));
      } else {
        navigate(nextgenProjectPath(response.project.id));
      }
    } catch (error) {
      if (isApiError(error) && error.status === 403) {
        showError("You don't have access to a dataset or agent this action needs.");
      } else if (isApiError(error) && error.status === 404) {
        showError("This quick action is no longer available.");
        void mutateQuickActionsAvailable();
      } else if (isApiError(error)) {
        showError(getApiErrorMessage(error));
      } else {
        showError("Something went wrong running that action.");
      }
    } finally {
      setInvokingId(null);
    }
  };

  return { invoke, invokingId };
};
